import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Popover,
} from '@material-ui/core'
import { ClosableDialog } from 'components/controls/dialogs'
import { PurchaseEventWizard } from 'components/domain/taxation/PurchaseEventWizard'
import { ActionButton, Button, SaveIcon } from 'components/facets'
import { useUser } from 'components/providers/UserProvider'
import { useConfirm } from 'lib/hooks'
import { useFetchPurchaseEvents } from 'lib/hooks/useFetchPurchaseEvents'
import { useInventoryStore } from 'lib/hooks/useInventoryStore'
import { MAX_LISTING_TABS } from 'lib/hooks/useListingTabsWithStocks'
import { useStockStore } from 'lib/hooks/useStockStore'
import { getArticleQty } from 'lib/utils'
import { useSnackbar } from 'notistack'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  commentSuggestsUsEdition,
  CountryEdition,
  InventoryArticle,
  INVENTORY_MULTI_SELECTION_UPDATES,
  INVENTORY_ZOOM_LEVELS,
  LinkArticleToPurchaseEvent,
  PurchaseEvent,
  STOCK_TYPES,
  TAXATION_SCHEME,
  TAXATION_SCHEME_CONVERSIONS,
} from 'shared'
import {
  LIMIT_OF_EDITED_ITEMS_THAT_CAN_BE_SAVED,
  LINKED_LOT_ICON,
  ListingTabsState,
  LISTING_AND_APPRAISAL,
  STOCK_DISPLAY,
} from 'utils/constants'
import { shallow } from 'zustand/shallow'
import { Filter } from '../FilterDock/Filter'
import { ActionEditorDialog } from './ActionEditorDialog'
import './actions-presenter.css'
import { MobileActionPresenterButton, MobileActionsPresenter } from './MobileActionsPresenter'

export type PriceTarget = 'sell' | 'buy'
export type ConfirmPayload =
  | { actionType: INVENTORY_MULTI_SELECTION_UPDATES.NEW_PRICE; target: PriceTarget; input: string }
  | { actionType: INVENTORY_MULTI_SELECTION_UPDATES.NEW_QUANTITY; input: string }
  | { actionType: INVENTORY_MULTI_SELECTION_UPDATES.NEW_COMMENT; input: string }
  | { actionType: INVENTORY_MULTI_SELECTION_UPDATES.NEW_LOCATION; input: string }
  | { actionType: INVENTORY_MULTI_SELECTION_UPDATES.NEW_CONDITION; input: string }
  | { actionType: INVENTORY_MULTI_SELECTION_UPDATES.NEW_TAXATION_SCHEME; input: string }

interface ActionsPresenterProps {
  listingTabsState: ListingTabsState | undefined
  selectedStockExists: boolean
  editedStockExists: boolean
  openAutopriceDialog: () => void
  updateAttributesOfMultiSelectionArticle: (
    name: INVENTORY_MULTI_SELECTION_UPDATES,
    value:
      | string
      | number
      | boolean
      | TAXATION_SCHEME
      | ((article: InventoryArticle) => number | string | boolean),
    all?: boolean
  ) => void
  revertArticleEdit: (article: InventoryArticle | 'all' | 'selected') => void
  openPublishDialog?: () => void
  unselectAll: () => void
  selectAllResults: () => void
  clearFailedAutoprice: (target: 'all' | 'selected') => void
  handleDeleteArticle: (article: InventoryArticle | 'all' | 'selected') => void
  selectedStockLength: number
  selectedArticles: InventoryArticle[]
  exportSelectedCsv: () => void
  disabled?: boolean
  disabledActions?: string[]
  parentPage: string
  showButtonRemoveComment: boolean
  setOpenCsvBulkEditDialog: React.Dispatch<React.SetStateAction<boolean>>
  handleConfirmLinkToLotByInventory: (
    selectedLot: PurchaseEvent,
    linkData?: LinkArticleToPurchaseEvent
  ) => void | Promise<void>
  handleConfirmLinkToLotByListing: (selectedLot: PurchaseEvent) => void
  moveSelectedItemsToTab?: (targetTabId: number) => void
  moveSelectedItemsToNewTab?: () => void
  editBulkBuyPrice?: (
    value:
      | string
      | number
      | boolean
      | TAXATION_SCHEME
      | ((article: InventoryArticle) => number | string | boolean | undefined)
  ) => { skipped: number } | void
}

export const ActionsPresenter = React.memo(function ActionsPresenter({
  listingTabsState,
  selectedStockExists,
  editedStockExists,
  openAutopriceDialog,
  updateAttributesOfMultiSelectionArticle,
  revertArticleEdit,
  openPublishDialog,
  selectAllResults,
  unselectAll,
  handleDeleteArticle,
  selectedStockLength,
  selectedArticles,
  exportSelectedCsv,
  parentPage,
  showButtonRemoveComment,
  setOpenCsvBulkEditDialog,
  handleConfirmLinkToLotByInventory,
  handleConfirmLinkToLotByListing,
  moveSelectedItemsToTab,
  moveSelectedItemsToNewTab,
  editBulkBuyPrice,
  disabled = false,
}: ActionsPresenterProps): JSX.Element {
  const { t } = useTranslation()
  const { purchaseEvents } = useFetchPurchaseEvents()

  const [type, setType] = useState<null | INVENTORY_MULTI_SELECTION_UPDATES>(null)
  const { enqueueSnackbar } = useSnackbar()
  const { user } = useUser()
  const [openActionsPresenterMobileDialog, setOpenActionsPresenterMobileDialog] = useState(false)
  const [editorOpen, setEditorOpen] = useState(false)
  const [openWizard, setOpenWizard] = useState(false)

  const [openMoveToTabDialog, setOpenMoveToTabDialog] = useState(false)
  const [moveDialogTabs, setMoveDialogTabs] = useState<ListingTabsState['tabs']>([])
  const [moveTarget, setMoveTarget] = useState<string>('')
  // FAB = Floating Action Button (action button in mobile layouts)
  const [showFabHint, setShowFabHint] = useState(
    () => localStorage.getItem('fabHintDismissed') === null
  )
  const [fabHintReady, setFabHintReady] = useState(false)

  useEffect(() => {
    if (!showFabHint) return
    const timer = setTimeout(() => setFabHintReady(true), 2000)
    return () => clearTimeout(timer)
  }, [showFabHint])

  const dismissFabHint = () => {
    if (!fabHintReady) return
    localStorage.setItem('fabHintDismissed', 'true')
    setShowFabHint(false)
  }

  const [zoomLevel] = useInventoryStore((s) => [s.zoomLevel], shallow)
  const [activeStockType] = useStockStore((s) => [s.activeStockType], shallow)

  // --- Adaptive toolbar collapse ---
  // Decides whether to show all action buttons inline or collapse them into a
  // dropdown (desktop Popover) / mobile overlay, based on actual available space.
  //
  // How it works:
  //  1. A ResizeObserver watches .stock-display (the flex parent with real width).
  //  2. When buttons are inline, useLayoutEffect measures the toolbar's scrollWidth
  //     and collapses BEFORE paint if it exceeds the available space (no flash).
  //  3. When collapsed, a useEffect re-expands once the window is wide enough.
  //  4. Adding/removing buttons is handled automatically — no manual counting needed.
  //
  // INLINE_MARGIN_RIGHT adds breathing room so buttons don't stick to the edge.

  const toolbarRef = useRef<HTMLDivElement>(null)
  const expandedScrollWidth = useRef(0)
  const [availableWidth, setAvailableWidth] = useState(window.innerWidth)
  const [showInlineActions, setShowInlineActions] = useState(true)
  const INLINE_MARGIN_RIGHT = 140 // takes into account the presence of the spinner and prevents it from overlaying the save button

  useEffect(() => {
    const stockDisplay = toolbarRef.current?.closest('.stock-display') as HTMLElement | null
    if (!stockDisplay) return
    const observer = new ResizeObserver((entries) => {
      if (entries[0]) setAvailableWidth(entries[0].contentRect.width)
    })
    observer.observe(stockDisplay)
    return () => observer.disconnect()
  }, [])

  useLayoutEffect(() => {
    if (showInlineActions && toolbarRef.current) {
      expandedScrollWidth.current = toolbarRef.current.scrollWidth
      if (expandedScrollWidth.current + INLINE_MARGIN_RIGHT > availableWidth) {
        setShowInlineActions(false)
      }
    }
  }, [showInlineActions, availableWidth, parentPage, activeStockType, editedStockExists])

  useEffect(() => {
    if (
      !showInlineActions &&
      expandedScrollWidth.current > 0 &&
      expandedScrollWidth.current + INLINE_MARGIN_RIGHT <= availableWidth
    ) {
      setShowInlineActions(true)
    }
  }, [availableWidth, showInlineActions])

  // When the set of visible buttons changes while collapsed, force a re-measure
  useEffect(() => {
    if (!showInlineActions) {
      expandedScrollWidth.current = 0
      setShowInlineActions(true)
    }
  }, [parentPage, activeStockType, editedStockExists])

  const [desktopActionsAnchor, setDesktopActionsAnchor] = useState<HTMLElement | null>(null)

  const handleOpenDesktopActions = (event: React.MouseEvent<HTMLElement>) => {
    if (!selectedStockExists) return
    setDesktopActionsAnchor(event.currentTarget as HTMLElement)
  }

  const handleCloseDesktopActions = () => {
    setDesktopActionsAnchor(null)
  }

  const [confirmRevertArticleEdit, ConfirmRevertArticleEditDialog] = useConfirm(
    t('stockPricing.actions.discard'),
    t('stockPricing.actions.discardChangesConfirm', { count: selectedStockLength }),
    () => revertArticleEdit('selected')
  )

  const [confirmDeleteArticle, ConfirmDeleteArticleDialog] = useConfirm(
    t('stockPricing.actions.delete'),
    t('stockPricing.actions.deleteConfirm', { count: selectedStockLength }),
    () => handleDeleteArticle('selected')
  )

  const commentNeedsUsEditionWarning = (comment: string): boolean =>
    commentSuggestsUsEdition(comment) &&
    selectedArticles.some((article) => article.countryEdition !== CountryEdition.US)

  const handleExportSelectedCsv = () => {
    if (!user?.subscription.featuresDisabled.includes('addArticles.exportCsv')) {
      exportSelectedCsv()
    }
  }

  const handleOpenAutopriceDialog = () => {
    if (!user?.subscription.featuresDisabled.includes('autopriceArticles.autoprice')) {
      openAutopriceDialog()
    }
  }

  const handleCsvBulkEditDialog = () => {
    setOpenCsvBulkEditDialog(true)
  }

  const tooltipSaveButton = () => {
    if (!selectedStockExists) return t('stockPricing.actions.disabledSaveButton')
    if (selectedStockLength > LIMIT_OF_EDITED_ITEMS_THAT_CAN_BE_SAVED && editedStockExists)
      return t('stockPricing.actions.tooMuchItemsToSave', {
        maxArticles: LIMIT_OF_EDITED_ITEMS_THAT_CAN_BE_SAVED,
      })
    return ''
  }

  const handleOpenPurchaseEventLinkingDialog = async () => {
    setOpenWizard(true)
  }

  function closeEditor() {
    setEditorOpen(false)
  }

  const handleOpenMoveToTabDialog = () => {
    if (!listingTabsState) return
    setMoveDialogTabs(listingTabsState.tabs)
    const availableTabs = listingTabsState.tabs.filter(
      (t) => t.lotId === undefined && t.id !== listingTabsState.activeTabId
    )
    if (user.featureFlags?.taxation) {
      const availableLots = purchaseEvents.filter(
        (item) =>
          !item.seller?.name?.includes('Listing') &&
          listingTabsState.tabs.some(
            (tab) => tab.lotId === item.id && tab.id !== listingTabsState.activeTabId
          )
      )
      if (availableTabs.length > 0) {
        setMoveTarget(`tab-${availableTabs[0].id}`)
      } else if (listingTabsState.tabs.length < MAX_LISTING_TABS) {
        setMoveTarget('new-tab')
      } else if (availableLots.length > 0) {
        setMoveTarget(`lot-${availableLots[0].id}`)
      }
    } else {
      if (availableTabs.length > 0) {
        setMoveTarget(`tab-${availableTabs[0].id}`)
      } else if (listingTabsState.tabs.length < MAX_LISTING_TABS) {
        setMoveTarget('new-tab')
      }
    }
    setOpenMoveToTabDialog(true)
  }

  function applyMultiSelectionUpdate(payload: ConfirmPayload) {
    if (payload.actionType === INVENTORY_MULTI_SELECTION_UPDATES.NEW_CONDITION) {
      updateAttributesOfMultiSelectionArticle(
        INVENTORY_MULTI_SELECTION_UPDATES.NEW_CONDITION,
        payload.input
      )
      return true
    }
    if (payload.actionType === INVENTORY_MULTI_SELECTION_UPDATES.NEW_PRICE) {
      let modifier = 1 // Multiplier for increase or decrease

      if (payload.input.includes('%')) {
        let inputValue = payload.input.replace('%', '')

        // Checks if the percentage value is positive or negative
        if (inputValue.startsWith('+')) {
          inputValue = inputValue.replace('+', '')
          modifier = 1
        } else if (inputValue.startsWith('-')) {
          inputValue = inputValue.replace('-', '')
          modifier = -1
        } else {
          // No sign means it's setting to that percentage of the current price
          modifier = 0
        }

        const percentageValue = parseFloat(inputValue.replace(',', '.'))

        if (payload.target === 'sell' && modifier === -1 && percentageValue === 100) {
          enqueueSnackbar(t('error.setPriceValidation'), { variant: 'error' })
          return
        }

        if (
          !isNaN(percentageValue) &&
          Math.abs(percentageValue) >= 0.02 &&
          Math.abs(percentageValue) < 1000000
        ) {
          if (payload.target === 'sell') {
            updateAttributesOfMultiSelectionArticle(
              INVENTORY_MULTI_SELECTION_UPDATES.NEW_PRICE,
              (article) => {
                const basePrice = Number(article.newPrice || article.price)

                let newPrice
                if (modifier === 0) {
                  // Set to percentage of the current price
                  newPrice = Math.round(basePrice * (percentageValue / 100) * 100) / 100
                } else {
                  // Increase or decrease by percentage
                  newPrice =
                    Math.round(basePrice * (1 + (modifier * percentageValue) / 100) * 100) / 100
                }
                return newPrice
              }
            )
          } else {
            if (editBulkBuyPrice) {
              const result = editBulkBuyPrice((article) => {
                const basePrice = Number(article.buyPrice ?? 0)
                // Skip articles with no base buy price: percentage has no meaning on 0
                if (basePrice === 0) return undefined
                let newPrice
                if (modifier === 0) {
                  // Set to percentage of the current price
                  newPrice = Math.round(basePrice * (percentageValue / 100) * 100) / 100
                } else {
                  // Increase or decrease by percentage
                  newPrice =
                    Math.round(basePrice * (1 + (modifier * percentageValue) / 100) * 100) / 100
                }
                return newPrice
              })
              if (result && result.skipped > 0) {
                enqueueSnackbar(
                  t('stockPricing.bulkEdit.buyPriceSkipped', { count: result.skipped }),
                  { variant: 'info' }
                )
              }
            }
            return true
          }
        } else {
          enqueueSnackbar(t('error.setPriceValidation'), { variant: 'error' })
          return
        }
      } else {
        // values without percentage
        const numericValue = parseFloat(payload.input.replace(',', '.'))
        const isBuy = payload.target === 'buy'
        const minValue = isBuy ? 0 : 0.02

        if (!isNaN(numericValue) && numericValue >= minValue && numericValue < 1000000) {
          if (payload.target === 'sell') {
            updateAttributesOfMultiSelectionArticle(
              INVENTORY_MULTI_SELECTION_UPDATES.NEW_PRICE,
              numericValue
            )
          } else {
            editBulkBuyPrice && editBulkBuyPrice(numericValue)
          }
        } else {
          enqueueSnackbar(t('error.setPriceValidation'), { variant: 'error' })
        }
      }
      return true
    } else if (payload.actionType === INVENTORY_MULTI_SELECTION_UPDATES.NEW_QUANTITY) {
      if (payload.input.includes('.') || payload.input.includes(',')) {
        enqueueSnackbar(t('error.setQuantityValidation'), { variant: 'error' })
        return
      }
      if (payload.input.includes('+')) {
        const value = Number(payload.input.replace('+', ''))

        // Relative deltas are computed against the visible quantity (newQuantity if the
        // article carries staged changes, else the saved quantity). Otherwise a
        // post-panel staged cell with display "0 → 2" would treat its baseline as 0,
        // and `-1` would land it at -1 instead of 1.
        updateAttributesOfMultiSelectionArticle(
          INVENTORY_MULTI_SELECTION_UPDATES.NEW_QUANTITY,
          (article) => getArticleQty(article) + value
        )
      } else if (payload.input.includes('-')) {
        const value = Number(payload.input.replace('-', ''))

        updateAttributesOfMultiSelectionArticle(
          INVENTORY_MULTI_SELECTION_UPDATES.NEW_QUANTITY,
          (article) => getArticleQty(article) - value
        )
      } else {
        const value = Number(payload.input)

        if (Number.isNaN(value)) {
          enqueueSnackbar(t('error.setQuantityValidation'), { variant: 'error' })
          return
        }

        updateAttributesOfMultiSelectionArticle(
          INVENTORY_MULTI_SELECTION_UPDATES.NEW_QUANTITY,
          value
        )
      }
      return true
    } else if (payload.actionType === INVENTORY_MULTI_SELECTION_UPDATES.NEW_LOCATION) {
      updateAttributesOfMultiSelectionArticle(
        INVENTORY_MULTI_SELECTION_UPDATES.NEW_LOCATION,
        payload.input
      )
      return true
    } else if (payload.actionType === INVENTORY_MULTI_SELECTION_UPDATES.NEW_COMMENT) {
      const input = payload.input.trim()
      // "+ text" appends to the comment each article currently shows instead of replacing it
      if (input.startsWith('+')) {
        const addition = input.substring(1).trim()
        if (!addition) return true
        updateAttributesOfMultiSelectionArticle(
          INVENTORY_MULTI_SELECTION_UPDATES.NEW_COMMENT,
          (article) => {
            const current = (article.newComment ?? article.comment ?? '').trim()
            return current ? `${current} ${addition}` : addition
          }
        )
      } else {
        updateAttributesOfMultiSelectionArticle(
          INVENTORY_MULTI_SELECTION_UPDATES.NEW_COMMENT,
          payload.input
        )
      }
      return true
    } else if (payload.actionType === INVENTORY_MULTI_SELECTION_UPDATES.NEW_TAXATION_SCHEME) {
      const value = TAXATION_SCHEME_CONVERSIONS.find((item) => item.label === payload.input)?.code
      updateAttributesOfMultiSelectionArticle(
        INVENTORY_MULTI_SELECTION_UPDATES.NEW_TAXATION_SCHEME,
        value as TAXATION_SCHEME
      )
      return true
    }
    return false
  }

  function handleConfirm(payload: ConfirmPayload) {
    if (!type) return

    const success = applyMultiSelectionUpdate(payload)

    if (success) {
      closeEditor()
    }
    // if invalid:
    // - the dialog remains open
    // - the user sees the error snackbar
  }

  const getDisabledTooltip = () => {
    if (!selectedStockExists) {
      return t('stockPricing.tooltips.mustSelectArticles')
    } else if (user?.subscription.featuresDisabled.includes('autopriceArticles.autoprice')) {
      return t('subscriptionUpgradeNeededNew')
    }
  }

  return (
    <>
      <div ref={toolbarRef} className="d-flex flex-row align-items-center">
        <ActionButton
          text={t('stockPricing.selectAllResults')}
          data-testid="select-all-results-btn"
          icon="fa-light fa-object-group"
          color="secondary"
          disabled={disabled}
          onClick={selectAllResults}
        />
        <ActionButton
          text={t('stockPricing.resetSelection')}
          data-testid="reset-selection-btn"
          icon="fa-light fa-square-dashed"
          color="secondary"
          disabled={disabled}
          onClick={unselectAll}
        />
        <ActionButton
          data-testid="export-selected-csv-btn"
          text={t('csv.exportSelected')}
          onClick={handleExportSelectedCsv}
          icon="fa-light fa-file-arrow-down"
          color="secondary"
          disabled={
            disabled ||
            !selectedStockExists ||
            user?.subscription.featuresDisabled.includes('addArticles.exportCsv')
          }
          disabledTooltip={t('subscriptionUpgradeNeededNew')}
        />

        {parentPage === STOCK_DISPLAY && (
          <ActionButton
            text={t('stockPricing.bulkEdit.csvBulkEdits')}
            onClick={handleCsvBulkEditDialog}
            data-testid="csv-bulk-edit"
            icon="fa-light fa-pen-field"
            color="secondary"
            disabled={
              disabled ||
              zoomLevel === INVENTORY_ZOOM_LEVELS.ARTICLE ||
              user?.subscription.featuresDisabled.includes('addArticles.exportCsv')
            }
            disabledTooltip={t('subscriptionUpgradeNeededNew')}
          />
        )}

        {!showInlineActions ? (
          <div className="d-flex d-none d-xl-block">
            <ActionButton
              text={t('stockPricing.bulkEdit.actions')}
              data-testid="desktop-actions-btn"
              onClick={handleOpenDesktopActions}
              disabled={disabled || !selectedStockExists}
              disabledTooltip={getDisabledTooltip()}
              color="secondary"
              icon="fa-regular fa-pen icon-pen"
            />
          </div>
        ) : (
          <>
            <ActionButton
              text={t('stockPricing.actions.discard')}
              data-testid="desktop-discard-changes"
              icon="fa-light fa-arrow-rotate-left"
              color="secondary"
              disabled={disabled || !selectedStockExists || !editedStockExists}
              disabledTooltip={t('stockPricing.tooltips.mustSelectArticles')}
              onClick={() => {
                confirmRevertArticleEdit()
              }}
            />
            {parentPage === LISTING_AND_APPRAISAL && (
              <ActionButton
                text={t('stockPricing.actions.delete')}
                data-testid="delete-articles-menu-btn"
                icon="fa-light fa-trash"
                color="secondary"
                disabled={disabled || !selectedStockExists}
                disabledTooltip={t('stockPricing.tooltips.mustSelectArticles')}
                onClick={() => confirmDeleteArticle()}
              />
            )}

            <ActionButton
              text={t('stockPricing.bulkEdit.setPrice')}
              onClick={() => {
                setType(INVENTORY_MULTI_SELECTION_UPDATES.NEW_PRICE)
                setEditorOpen(true)
              }}
              icon="fa-light fa-tag"
              data-testid="desktop-set-price"
              color="secondary"
              disabled={disabled || !selectedStockExists}
              disabledTooltip={t('stockPricing.tooltips.mustSelectArticles')}
            />

            {parentPage !== LISTING_AND_APPRAISAL && (
              <ActionButton
                text={t('stockPricing.bulkEdit.setQuantity')}
                onClick={() => {
                  setType(INVENTORY_MULTI_SELECTION_UPDATES.NEW_QUANTITY)
                  setEditorOpen(true)
                }}
                icon="fa-light fa-plus-minus"
                data-testid="desktop-set-quantity"
                color="secondary"
                disabled={
                  disabled || !selectedStockExists || zoomLevel === INVENTORY_ZOOM_LEVELS.ARTICLE
                }
                disabledTooltip={t('stockPricing.tooltips.mustSelectArticlesAndExpandLocations')}
              />
            )}

            {activeStockType === STOCK_TYPES.SINGLES && (
              <ActionButton
                text={t('stockPricing.bulkEdit.setCondition')}
                onClick={() => {
                  setType(INVENTORY_MULTI_SELECTION_UPDATES.NEW_CONDITION)
                  setEditorOpen(true)
                }}
                icon="fa-light fa-ranking-star"
                data-testid="desktop-set-condition"
                color="secondary"
                disabled={
                  disabled || !selectedStockExists || zoomLevel === INVENTORY_ZOOM_LEVELS.ARTICLE
                }
                disabledTooltip={t('stockPricing.tooltips.mustSelectArticlesAndExpandLocations')}
              />
            )}

            <ActionButton
              text={t('stockPricing.bulkEdit.setLocation')}
              onClick={() => {
                setType(INVENTORY_MULTI_SELECTION_UPDATES.NEW_LOCATION)
                setEditorOpen(true)
              }}
              icon="fa-light fa-location-pen"
              data-testid="desktop-set-location"
              color="secondary"
              disabled={
                disabled || !selectedStockExists || zoomLevel === INVENTORY_ZOOM_LEVELS.ARTICLE
              }
              disabledTooltip={t('stockPricing.tooltips.mustSelectArticlesAndExpandLocations')}
            />

            <ActionButton
              text={t('stockPricing.bulkEdit.setComment')}
              onClick={() => {
                setType(INVENTORY_MULTI_SELECTION_UPDATES.NEW_COMMENT)
                setOpenActionsPresenterMobileDialog(false)
                setEditorOpen(true)
              }}
              icon="fa-light fa-message-dots"
              data-testid="desktop-set-comment"
              color="secondary"
              disabled={disabled || !selectedStockExists}
              disabledTooltip={t('addArticles.commentDescription')}
            />

            <ActionButton
              text={t('stockPricing.actions.autopriceStock')}
              onClick={handleOpenAutopriceDialog}
              data-testid="desktop-autoprice"
              disabled={
                disabled ||
                !selectedStockExists ||
                user?.subscription.featuresDisabled.includes('autopriceArticles.autoprice')
              }
              disabledTooltip={getDisabledTooltip()}
              icon="fa-light fa-money-check-dollar-pen"
              color="secondary"
              wordBreak="break-all"
            />

            {parentPage === LISTING_AND_APPRAISAL && (
              <ActionButton
                data-testid="desktop-move-to-tab"
                onClick={handleOpenMoveToTabDialog}
                color="secondary"
                disabled={disabled || !selectedStockExists}
                text={t('stockPricing.bulkEdit.moveToOtherTab')}
                icon="fa-light fa-folder-arrow-down"
              />
            )}

            {user.featureFlags?.taxation && parentPage === STOCK_DISPLAY && (
              <ActionButton
                data-testid="desktop-associate-lot"
                text={t('taxation.associatePe')}
                icon="fa-light fa-boxes-stacked"
                onClick={() => {
                  handleCloseDesktopActions()
                  handleOpenPurchaseEventLinkingDialog()
                }}
                color="secondary"
                disabled={disabled || !selectedStockExists}
              />
            )}

            {user.featureFlags?.taxation && user.enableMarginTaxation && (
              <ActionButton
                data-testid="desktop-update-taxation-scheme"
                text={t('taxation.setTaxationScheme')}
                icon="fa-light fa-money-bill-transfer"
                onClick={() => {
                  setType(INVENTORY_MULTI_SELECTION_UPDATES.NEW_TAXATION_SCHEME)
                  handleCloseDesktopActions()
                  setEditorOpen(true)
                }}
                color="secondary"
                disabled={disabled || !selectedStockExists}
              />
            )}
          </>
        )}

        <Button
          onClick={() => openPublishDialog && openPublishDialog()}
          disabled={
            disabled ||
            !selectedStockExists ||
            selectedStockLength > LIMIT_OF_EDITED_ITEMS_THAT_CAN_BE_SAVED
          }
          variant="contained"
          startIcon={<SaveIcon />}
          data-testid="publish-changes-btn"
          style={{
            minWidth: '90px',
            height: '40px',
            padding: '16px 11px',
            marginLeft: '1em',
          }}
          tooltip={tooltipSaveButton()}
        >
          {t('stockPricing.actions.confirmAndSave')}
        </Button>

        <ConfirmRevertArticleEditDialog />
        <ConfirmDeleteArticleDialog />

        {user.featureFlags?.taxation && parentPage === STOCK_DISPLAY && (
          <PurchaseEventWizard
            open={openWizard}
            onClose={() => setOpenWizard(false)}
            articles={selectedArticles}
            onConfirm={handleConfirmLinkToLotByInventory}
          />
        )}

        {/** MOVE BETWEEN TABS */}
        {listingTabsState && (
          <ClosableDialog open={openMoveToTabDialog} onClose={() => setOpenMoveToTabDialog(false)}>
            <div className="p-3">
              <DialogTitle disableTypography className="mb-3">
                <h3>Move selected items to:</h3>
              </DialogTitle>
              <DialogContent>
                <Filter
                  type="select"
                  setFilter={(_name, newValue) => setMoveTarget(newValue)}
                  label=""
                  name="move-tab"
                  defaultValue={moveTarget}
                  menuitems={
                    user.featureFlags?.taxation
                      ? moveDialogTabs
                          .filter(
                            (item) =>
                              item.lotId === undefined && item.id !== listingTabsState.activeTabId
                          )
                          .map((tab) => (
                            <MenuItem key={tab.id} value={`tab-${tab.id}`}>
                              <>
                                <Box width={30}></Box>
                                {tab.title && tab.title.trim() !== ''
                                  ? tab.title
                                  : t('stockPricing.tabDefault')}
                              </>
                            </MenuItem>
                          ))
                          .concat(
                            purchaseEvents
                              .filter(
                                (item) =>
                                  !item.seller?.name?.includes('Listing') &&
                                  moveDialogTabs.some(
                                    (tab) =>
                                      tab.lotId === item.id &&
                                      tab.id !== listingTabsState.activeTabId
                                  )
                              )
                              .map((item) => (
                                <MenuItem key={item.id} value={`lot-${item.id}`}>
                                  <>
                                    <Box width={30}>
                                      <img src={LINKED_LOT_ICON} width={25} />
                                    </Box>
                                    Lot {item.lotNumber} - {item.seller?.name} - {item.price} €
                                  </>
                                </MenuItem>
                              ))
                          )
                          .concat(
                            moveDialogTabs.length < MAX_LISTING_TABS
                              ? [
                                  <MenuItem key="new-tab" value={'new-tab'}>
                                    <>
                                      <Box width={30}></Box>
                                      {t('inventory.tabs.newTab')}...
                                    </>
                                  </MenuItem>,
                                ]
                              : []
                          )
                      : moveDialogTabs
                          .filter(
                            (item) =>
                              item.lotId === undefined && item.id !== listingTabsState.activeTabId
                          )
                          .map((tab) => (
                            <MenuItem key={tab.id} value={`tab-${tab.id}`}>
                              <>
                                <Box width={30}></Box>
                                {tab.title && tab.title.trim() !== ''
                                  ? tab.title
                                  : t('stockPricing.tabDefault')}
                              </>
                            </MenuItem>
                          ))
                          .concat(
                            moveDialogTabs.length < MAX_LISTING_TABS
                              ? [
                                  <MenuItem key="new-tab" value={'new-tab'}>
                                    <>
                                      <Box width={30}></Box>
                                      {t('inventory.tabs.newTab')}...
                                    </>
                                  </MenuItem>,
                                ]
                              : []
                          )
                  }
                />
              </DialogContent>

              <DialogActions>
                <div className="w-100 d-flex justify-content-end mt-3">
                  <Button
                    variant="outlined"
                    className="me-3"
                    onClick={() => {
                      setOpenMoveToTabDialog(false)
                      setMoveTarget('')
                    }}
                  >
                    {t('common.close')}
                  </Button>
                  <Button
                    variant="contained"
                    data-testid="confirm-move-tab"
                    disabled={moveTarget === ''}
                    onClick={() => {
                      if (!moveTarget) return

                      if (moveTarget === 'new-tab') {
                        moveSelectedItemsToNewTab?.()
                        setOpenMoveToTabDialog(false)
                        setMoveTarget('')
                        handleCloseDesktopActions()
                        return
                      }

                      if (moveTarget.startsWith('tab-') && moveSelectedItemsToTab) {
                        const tabId = Number(moveTarget.replace('tab-', ''))
                        moveSelectedItemsToTab(tabId)
                        setOpenMoveToTabDialog(false)
                        setMoveTarget('')
                        handleCloseDesktopActions()
                        return
                      }

                      if (moveTarget.startsWith('lot-')) {
                        const lotId = Number(moveTarget.replace('lot-', ''))
                        const lot = purchaseEvents.find((p) => p.id === lotId)

                        if (!lot) {
                          enqueueSnackbar(t('stockPricing.errors.selectedPurchaseEventNotFound'), {
                            variant: 'error',
                          })
                          return
                        }
                        handleConfirmLinkToLotByListing(lot)
                      }

                      setOpenMoveToTabDialog(false)
                      setMoveTarget('')
                      handleCloseDesktopActions()
                    }}
                  >
                    {t('common.confirm')}
                  </Button>
                </div>
              </DialogActions>
            </div>
          </ClosableDialog>
        )}
      </div>

      {!showInlineActions && (
        <>
          <div
            className={`floating-actions-presenter action ${
              !selectedStockExists || disabled ? 'disabled' : ''
            } d-xl-none ${openActionsPresenterMobileDialog ? 'open' : ''}`}
            role="button"
            data-testid="floating-action-button"
            onClick={
              selectedStockExists && !disabled
                ? () => {
                    dismissFabHint()
                    setOpenActionsPresenterMobileDialog((prev) => !prev)
                  }
                : undefined
            }
          >
            <i className="fa-regular fa-pen icon-pen" aria-hidden="true" />
          </div>

          {showFabHint && !openActionsPresenterMobileDialog && (
            <>
              <div className="fab-hint-overlay d-xl-none" onClick={dismissFabHint} />
              <div className="fab-hint d-xl-none" onClick={dismissFabHint}>
                <span>{t('stockPricing.fabHint')}</span>
                <div className="fab-hint-arrow" />
              </div>
            </>
          )}

          <MobileActionsPresenter
            user={user}
            onClose={() => setOpenActionsPresenterMobileDialog(false)}
            openActionsPresenterMobileDialog={openActionsPresenterMobileDialog}
            onPickAction={(value) => {
              setType(value)
              setOpenActionsPresenterMobileDialog(false)
              setEditorOpen(true)
            }}
            parentPage={parentPage}
            handleOpenAutopriceDialog={handleOpenAutopriceDialog}
            handleCloseDesktopActions={handleCloseDesktopActions}
            handleOpenPurchaseEventLinkingDialog={handleOpenPurchaseEventLinkingDialog}
            setOpenMoveToTabDialog={handleOpenMoveToTabDialog}
            onDiscard={() => confirmRevertArticleEdit()}
            onDelete={() => confirmDeleteArticle()}
            editedStockExists={editedStockExists}
            activeStockType={activeStockType}
            zoomLevel={zoomLevel}
          />

          <Popover
            open={Boolean(desktopActionsAnchor)}
            anchorEl={desktopActionsAnchor}
            onClose={handleCloseDesktopActions}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              elevation: 0,
              style: {
                background: 'transparent',
                boxShadow: 'none',
                padding: 0,
              },
            }}
          >
            <div className="desktop-actions-list d-flex flex-column align-items-end">
              <MobileActionPresenterButton
                dataTestid="desktop-set-price"
                label={t('stockPricing.bulkEdit.setPrice')}
                iconClass="fa-light fa-tag"
                onClick={() => {
                  setType(INVENTORY_MULTI_SELECTION_UPDATES.NEW_PRICE)
                  handleCloseDesktopActions()
                  setEditorOpen(true)
                }}
              />

              {parentPage !== LISTING_AND_APPRAISAL && (
                <MobileActionPresenterButton
                  dataTestid="desktop-set-quantity"
                  label={t('stockPricing.bulkEdit.setQuantity')}
                  iconClass="fa-light fa-plus-minus"
                  onClick={() => {
                    setType(INVENTORY_MULTI_SELECTION_UPDATES.NEW_QUANTITY)
                    handleCloseDesktopActions()
                    setEditorOpen(true)
                  }}
                  disabled={zoomLevel === INVENTORY_ZOOM_LEVELS.ARTICLE}
                />
              )}

              {activeStockType === STOCK_TYPES.SINGLES && (
                <MobileActionPresenterButton
                  dataTestid="desktop-set-condition"
                  label={t('stockPricing.bulkEdit.setCondition')}
                  iconClass="fa-light fa-ranking-star"
                  onClick={() => {
                    setType(INVENTORY_MULTI_SELECTION_UPDATES.NEW_CONDITION)
                    handleCloseDesktopActions()
                    setEditorOpen(true)
                  }}
                  disabled={zoomLevel === INVENTORY_ZOOM_LEVELS.ARTICLE}
                />
              )}

              <MobileActionPresenterButton
                dataTestid="desktop-set-location"
                label={t('stockPricing.bulkEdit.setLocation')}
                iconClass="fa-light fa-location-pen"
                onClick={() => {
                  setType(INVENTORY_MULTI_SELECTION_UPDATES.NEW_LOCATION)
                  handleCloseDesktopActions()
                  setEditorOpen(true)
                }}
                disabled={zoomLevel === INVENTORY_ZOOM_LEVELS.ARTICLE}
              />

              <MobileActionPresenterButton
                dataTestid="desktop-set-comment"
                label={t('stockPricing.bulkEdit.setComment')}
                iconClass="fa-light fa-message-dots"
                onClick={() => {
                  setType(INVENTORY_MULTI_SELECTION_UPDATES.NEW_COMMENT)
                  handleCloseDesktopActions()
                  setEditorOpen(true)
                }}
              />

              <MobileActionPresenterButton
                dataTestid="desktop-autoprice"
                label={t('stockPricing.actions.autopriceStock')}
                iconClass="fa-light fa-money-check-dollar-pen"
                onClick={() => {
                  handleCloseDesktopActions()
                  handleOpenAutopriceDialog()
                }}
              />

              {editedStockExists && (
                <MobileActionPresenterButton
                  dataTestid="desktop-discard-changes"
                  label={t('stockPricing.actions.discard')}
                  iconClass="fa-light fa-arrow-rotate-left"
                  onClick={() => {
                    handleCloseDesktopActions()
                    confirmRevertArticleEdit()
                  }}
                />
              )}

              {parentPage === LISTING_AND_APPRAISAL && (
                <MobileActionPresenterButton
                  dataTestid="delete-articles-menu-btn"
                  label={t('stockPricing.actions.delete')}
                  iconClass="fa-light fa-trash"
                  onClick={() => {
                    handleCloseDesktopActions()
                    confirmDeleteArticle()
                  }}
                />
              )}

              {user.featureFlags?.taxation && parentPage === STOCK_DISPLAY && (
                <MobileActionPresenterButton
                  dataTestid="desktop-associate-lot"
                  label={t('taxation.associatePe')}
                  iconClass="fa-light fa-boxes-stacked"
                  onClick={() => {
                    handleCloseDesktopActions()
                    handleOpenPurchaseEventLinkingDialog()
                  }}
                />
              )}

              {user.featureFlags?.taxation && user.enableMarginTaxation && (
                <MobileActionPresenterButton
                  dataTestid="desktop-update-taxation-scheme"
                  label={t('taxation.setTaxationScheme')}
                  iconClass="fa-light fa-money-bill-transfer"
                  onClick={() => {
                    setType(INVENTORY_MULTI_SELECTION_UPDATES.NEW_TAXATION_SCHEME)
                    handleCloseDesktopActions()
                    setEditorOpen(true)
                  }}
                />
              )}

              {parentPage === LISTING_AND_APPRAISAL && (
                <MobileActionPresenterButton
                  dataTestid="desktop-move-to-tab"
                  label={t('stockPricing.bulkEdit.moveToOtherTab')}
                  iconClass="fa-light fa-folder-arrow-down"
                  onClick={handleOpenMoveToTabDialog}
                />
              )}
            </div>
          </Popover>
        </>
      )}

      {editorOpen && (
        <ActionEditorDialog
          open={editorOpen}
          type={type}
          onClose={closeEditor}
          parentPage={parentPage}
          onConfirm={handleConfirm}
          commentNeedsUsEditionWarning={commentNeedsUsEditionWarning}
          showButtonRemoveComment={showButtonRemoveComment}
          showBuyPriceOption={!!user.featureFlags?.taxation}
        />
      )}
    </>
  )
})
